import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Chip,
  Button,
  Tooltip,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Milestone } from "../../features/projects/Milestone";
import {
  computeSnapshotComparison,
  getMilestoneDateRange,
  MetricComparison,
} from "../../features/snapshotComparison/snapshotComparisonUtils";

interface SnapshotComparisonProps {
  selectedMilestone: Milestone;
}

const METRIC_TOOLTIPS: Record<string, string> = {
  "Total Estimate":
    "The milestone budget (hours) from [ESTIMATED_HOURS] in the milestone description. This is the number communicated to the client.",
  "Actual Burnup":
    "Cumulative actual hours logged in Clockify for this milestone. Shows real work done over time.",
  "Estimate Burnup":
    "Projected hours completed based on task completion ratio: Total Estimate x (Completed Tasks / Total Tasks).",
  "Est. Total Hours":
    "Predicted total hours needed to finish the milestone: Actual Burnup x (Milestone Estimate / Completed Task Estimates). Decreasing means improving efficiency.",
  "Total Task Estimate":
    "Sum of individual task time estimates (hours) for all tasks created up to each date.",
  "Total Of Est. Completed":
    "Sum of time estimates (hours) for tasks that have been closed/completed.",
  "Total Tasks":
    "Count of tasks created in this milestone up to each date.",
};

const GREEN_BG = "rgba(75, 192, 192, 0.15)";
const GREEN_TEXT = "rgba(75, 192, 192, 1)";
const RED_BG = "rgba(255, 99, 132, 0.15)";
const RED_TEXT = "rgba(255, 99, 132, 1)";
const GRAY_BG = "rgba(201, 203, 207, 0.15)";
const GRAY_TEXT = "rgba(201, 203, 207, 1)";

function getDeltaColors(metric: MetricComparison): { bg: string; text: string } {
  if (metric.delta == null || metric.delta === 0) {
    return { bg: GRAY_BG, text: GRAY_TEXT };
  }
  if (metric.polarity === "neutral") {
    return { bg: GRAY_BG, text: GRAY_TEXT };
  }
  // For "positive" polarity: increase is good (green), decrease is bad (red)
  // For "negative" polarity: decrease is good (green), increase is bad (red)
  const isGood =
    metric.polarity === "positive" ? metric.delta > 0 : metric.delta < 0;
  return isGood
    ? { bg: GREEN_BG, text: GREEN_TEXT }
    : { bg: RED_BG, text: RED_TEXT };
}

function formatValue(metric: MetricComparison, value: number | undefined | null): string {
  if (value == null) return "N/A";
  return metric.isCount ? value.toFixed(0) : `${value.toFixed(2)}h`;
}

function formatDelta(metric: MetricComparison, delta: number): string {
  const prefix = delta > 0 ? "+" : "";
  return metric.isCount
    ? `${prefix}${delta.toFixed(0)}`
    : `${prefix}${delta.toFixed(2)}h`;
}

function subtractDays(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() - days);
  return d.toISOString().split("T")[0];
}

const SnapshotComparison = ({ selectedMilestone }: SnapshotComparisonProps) => {
  const dateRange = useMemo(
    () => getMilestoneDateRange(selectedMilestone),
    [selectedMilestone]
  );

  const defaultDateA = dateRange ? subtractDays(dateRange.max, 7) : "";
  const defaultDateB = dateRange?.max || "";

  const [dateA, setDateA] = useState(defaultDateA);
  const [dateB, setDateB] = useState(defaultDateB);

  // Reset dates when milestone changes
  useEffect(() => {
    if (dateRange) {
      setDateA(subtractDays(dateRange.max, 7));
      setDateB(dateRange.max);
    }
  }, [selectedMilestone.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const comparison = useMemo(() => {
    if (!dateA || !dateB) return null;
    return computeSnapshotComparison(selectedMilestone, dateA, dateB);
  }, [selectedMilestone, dateA, dateB]);

  if (!dateRange) return null;

  return (
    <Card elevation={2} sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Snapshot Comparison
        </Typography>

        {/* Date pickers and quick-select */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <TextField
            type="date"
            size="small"
            label="Date A"
            value={dateA}
            onChange={(e) => setDateA(e.target.value)}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { min: dateRange.min, max: dateRange.max },
            }}
            sx={{ width: 170 }}
          />
          <ArrowForwardIcon sx={{ color: "text.secondary" }} />
          <TextField
            type="date"
            size="small"
            label="Date B"
            value={dateB}
            onChange={(e) => setDateB(e.target.value)}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { min: dateRange.min, max: dateRange.max },
            }}
            sx={{ width: 170 }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setDateA(subtractDays(dateRange.max, 7));
                setDateB(dateRange.max);
              }}
            >
              7 days
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setDateA(subtractDays(dateRange.max, 30));
                setDateB(dateRange.max);
              }}
            >
              30 days
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setDateA(dateRange.min);
                setDateB(dateRange.max);
              }}
            >
              Full range
            </Button>
          </Box>
        </Box>

        {/* Comparison rows */}
        {comparison && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {comparison.metrics.map((metric) => (
              <Box
                key={metric.label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  px: 1.5,
                  borderLeft: `3px solid ${metric.borderColor}`,
                  backgroundColor: metric.bgColor,
                  borderRadius: "0 4px 4px 0",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 150, flexShrink: 0 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {metric.label}
                  </Typography>
                  {METRIC_TOOLTIPS[metric.label] && (
                    <Tooltip title={METRIC_TOOLTIPS[metric.label]} arrow>
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                    </Tooltip>
                  )}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flex: 1,
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", minWidth: 70, textAlign: "right" }}
                  >
                    {formatValue(metric, metric.snapshotA?.value)}
                  </Typography>
                  <ArrowForwardIcon
                    sx={{ fontSize: 16, color: "text.disabled" }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ minWidth: 70 }}
                  >
                    {formatValue(metric, metric.snapshotB?.value)}
                  </Typography>
                </Box>

                <Box sx={{ minWidth: 100, display: "flex", justifyContent: "flex-end" }}>
                  {metric.delta != null ? (
                    <Chip
                      label={
                        metric.deltaPercent != null
                          ? `${formatDelta(metric, metric.delta)} (${metric.deltaPercent > 0 ? "+" : ""}${metric.deltaPercent.toFixed(1)}%)`
                          : formatDelta(metric, metric.delta)
                      }
                      size="small"
                      sx={{
                        fontWeight: 600,
                        backgroundColor: getDeltaColors(metric).bg,
                        color: getDeltaColors(metric).text,
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      --
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SnapshotComparison;
